import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Apple, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { C } from "./data";
import { Wordmark, Serif } from "./ui";
import { getMobileOS, getDesktopOS } from "./device";

const IOS_STEP = { icon: Apple, label: "iPhone", steps: "open in Safari → Share → Add to Home Screen" };
const ANDROID_STEP = { icon: Smartphone, label: "Android", steps: "open in Chrome → menu (⋮) → Install app" };

function Step({ item }) {
  const Icon = item.icon;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.slate }}>
      <Icon size={15} color={C.ink} /> <span><b style={{ color: C.ink }}>{item.label}:</b> {item.steps}</span>
    </div>
  );
}

export default function DesktopGate() {
  // Land the phone on the install step, not just the app shell.
  const url = (() => {
    const u = new URL(window.location.href);
    u.searchParams.set("install", "1");
    return u.toString();
  })();
  const [copied, setCopied] = useState(false);
  // Best guess for which phone the user will use, so we show only the relevant steps.
  const mobile = getMobileOS();
  const desk = getDesktopOS();
  const guess = mobile !== "other" ? mobile : desk === "mac" ? "ios" : desk === "windows" ? "android" : "both";
  const [platform, setPlatform] = useState(guess);

  const copy = () =>
    navigator.clipboard.writeText(url)
      .then(() => { setCopied(true); toast.success("Link copied — open it on your phone"); setTimeout(() => setCopied(false), 2200); })
      .catch(() => toast.error("Copy failed — select the address bar and copy manually"));

  return (
    <div data-testid="desktop-gate" style={{ minHeight: "100dvh", background: "linear-gradient(160deg,#0d1826 0%,#0B1E3B 55%,#12263f 100%)", fontFamily: "'Inter', system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: 460, maxWidth: "100%", background: C.body, borderRadius: 20, padding: "34px 30px", boxShadow: "0 30px 80px rgba(0,0,0,0.45)" }}>
        <Wordmark />
        <Serif size={28} style={{ margin: "22px 0 8px" }}>ResQ1 lives on your <em style={{ color: C.slate }}>phone</em>.</Serif>
        <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.6, marginBottom: 22 }}>
          ResQ1 is an emergency app — it needs your phone's GPS and notifications to send responders to you. Please set it up on your mobile, not this computer.
        </p>

        <div style={{ display: "flex", gap: 20, alignItems: "center", background: "#fff", border: "1px solid rgba(26,30,36,0.1)", borderRadius: 14, padding: 18 }}>
          <div style={{ background: "#fff", padding: 6, borderRadius: 8, flexShrink: 0 }}>
            <QRCodeSVG value={url} size={132} level="M" fgColor={C.ink} bgColor="#ffffff" data-testid="desktop-gate-qr" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 4 }}>Scan to open on your phone</div>
            <div style={{ fontSize: 12.5, color: C.slate, lineHeight: 1.5 }}>Point your phone camera at this code, tap the link, then add ResQ1 to your home screen.</div>
          </div>
        </div>

        <button onClick={copy} data-testid="desktop-gate-copy"
          style={{ marginTop: 14, width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 13, background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Link copied" : "Copy link to send to my phone"}
        </button>

        <div style={{ marginTop: 18, display: "grid", gap: 8 }} data-testid="desktop-gate-steps">
          {platform === "both" ? (
            <><Step item={IOS_STEP} /><Step item={ANDROID_STEP} /></>
          ) : platform === "ios" ? (
            <>
              <Step item={IOS_STEP} />
              <button onClick={() => setPlatform("android")} data-testid="gate-switch-android" style={{ justifySelf: "start", background: "none", border: 0, padding: 0, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Using an Android phone?</button>
            </>
          ) : (
            <>
              <Step item={ANDROID_STEP} />
              <button onClick={() => setPlatform("ios")} data-testid="gate-switch-ios" style={{ justifySelf: "start", background: "none", border: 0, padding: 0, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Using an iPhone?</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
